import React from "react";

import {
  MediaTimelineCategory,
  MediaTimelineEvent,
} from "@pages/VideoSessionsV2/components/MediaTimeline/MediaTimeline";
import { getColorForGoal, getRowBackground } from "@pages/VideoSessionsV2/components/MediaTimeline/helper";
import { useVideoSessionData } from "@providers/VideoSessionProviderV2";

interface MomentDataRowsProps {
  data: MediaTimelineCategory[];
  expandedRowIds: string[];
  onToggleExpand: (id: string) => void;
  unpinnedSectionCollapsed: boolean;
  setUnpinnedSectionCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  getXPosition: (time: number) => number;
  getWidth: (start: number, end: number) => number;
  onMomentClicked: (startTime: number, endTime: number) => void;
}

export const MomentDataRows: React.FC<MomentDataRowsProps> = ({
  data,
  expandedRowIds,
  onToggleExpand,
  unpinnedSectionCollapsed,
  setUnpinnedSectionCollapsed,
  getWidth,
  getXPosition,
  onMomentClicked,
}) => {
  // Split data into pinned and unpinned categories
  const pinnedData = data.filter((cat) => cat.isPinned);
  const unpinnedData = data.filter((cat) => !cat.isPinned);
  const hasBothSections = pinnedData.length > 0 && unpinnedData.length > 0;

  const {
    setActiveMomentId,
    setActiveMomentTargetId,
    setActiveMomentGoalId,
    activeMomentGoalId,
    mediaClassificationsData,
  } = useVideoSessionData();

  // Handle click on moment data row, convert click position to timestamp based on start/end of event
  const handleMomentDataRowClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, event: MediaTimelineEvent) => {
    const rect = (e.target as HTMLDivElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    const timestamp = event.start + percent * (event.end - event.start);
    onMomentClicked(timestamp, event.end);
    if (event.goalId) {
      setActiveMomentGoalId(event.goalId);
    }
    if (!event.aggregated) {
      setActiveMomentId(event.id);
      if (event.targetId) {
        setActiveMomentTargetId(event.targetId);
      }
    } else if (event.goalId && !expandedRowIds.includes(event.goalId)) {
      onToggleExpand(event.goalId);
    }
  };

  const renderCategory = (cat: (typeof data)[number]) => {
    const isExpanded = expandedRowIds.includes(cat.id);
    const categoryEvents = [...cat.events.map((e) => ({ ...e, greyedOut: isExpanded }))];

    return (
      <React.Fragment key={cat.id}>
        <MomentDataRow events={categoryEvents} onMomentClick={handleMomentDataRowClick} />
        {isExpanded &&
          cat.children &&
          cat.children.map((child) => (
            <MomentDataRow key={child.id} events={child.events} onMomentClick={handleMomentDataRowClick} />
          ))}
      </React.Fragment>
    );
  };
  const MomentDataRow = ({
    events,
    onMomentClick,
  }: {
    events: MediaTimelineEvent[];
    onMomentClick: (e: React.MouseEvent<HTMLDivElement>, event: MediaTimelineEvent) => void;
  }) => {
    return (
      <div className="relative border-b h-10">
        {events.map((e) => (
          <div
            key={e.id}
            data-timeline-moment-id={`${activeMomentGoalId}-${e.id}`}
            className="absolute h-6 rounded-sm cursor-pointer"
            style={{
              left: getXPosition(e.start),
              width: getWidth(e.start, e.end),
              top: 7,
              ...getRowBackground(e.color, e.greyedOut || false),
              border: e.aggregated && e.goalId === activeMomentGoalId ? "1px solid black" : undefined,
            }}
            onClick={(mouseEvent) => onMomentClick(mouseEvent, e)}
          />
        ))}
      </div>
    );
  };

  const renderSignalCategory = (signal: (typeof signalData)[number]) => {
    return (
      <MomentDataRow
        key={signal.id}
        events={signal.events.map((i) => ({ ...i, goalId: "crying-and-screaming" }))}
        onMomentClick={handleMomentDataRowClick}
      />
    );
  };

  const signalData = [
    {
      id: "crying-and-screaming",
      title: "Crying and Screaming",
      events: mediaClassificationsData.map((i) => ({
        id: i.id,
        start: i.startTime,
        end: i.endTime,
        color: getColorForGoal("Crying and Screaming"),
        greyedOut: false,
        aggregated: false,
      })),
    },
  ];

  return (
    <>
      {pinnedData.map(renderCategory)}

      {/* Render divider if there are both sections */}
      {hasBothSections && (
        <div
          className="relative border-b h-[10px] bg-limestone-200 cursor-pointer flex items-center"
          onClick={() => setUnpinnedSectionCollapsed(!unpinnedSectionCollapsed)}
        />
      )}
      {/* Render unpinned goals if not collapsed */}
      {!unpinnedSectionCollapsed && (
        <>
          {mediaClassificationsData?.length > 0 && signalData.map(renderSignalCategory)}{" "}
          {unpinnedData.map(renderCategory)}
        </>
      )}
    </>
  );
};
